import { useAuth } from '@clerk/clerk-react';
import { useApiClient } from '@startupquickstart/frontend';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { describeElement, findClickable } from '@/lib/analytics/describeElement';
import { AnalyticsQueue } from '@/lib/analytics/queue';
import { redactPath } from '@/lib/analytics/redact';
import { getSessionId } from '@/lib/analytics/session';
import {
  ANALYTICS_EVENT_NAME,
  ANALYTICS_SOURCE_PORTAL,
  type AnalyticsEvent,
  type AnalyticsEventName,
  type AnalyticsProperties,
} from '@/lib/analytics/types';

/** Ingest path, relative to the API client's `/api/v1` base. */
const INGEST_ENDPOINT = '/analytics/events';

/** How often queued events are shipped. */
const FLUSH_INTERVAL_MS = 5_000;

/**
 * Records what people actually do in the portal, so repetitive workflows can be
 * found and automated rather than guessed at.
 *
 * Capture is deliberately structural. Route changes and clicks on interactive
 * controls are recorded; the text of those controls is read only where it is
 * developer-authored or explicitly labelled, and everything captured is
 * redacted (see `redact.ts` and `describeElement.ts`). Form values are never
 * read at all. Put `data-analytics-ignore` on any subtree that should be
 * invisible to this, and `data-analytics-id` on a control that deserves a
 * stable name.
 *
 * Renders nothing and never suspends: telemetry must not be able to delay,
 * break, or re-render the application it is measuring.
 */
export function AnalyticsProvider({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactElement {
  const apiClient = useApiClient();
  const { isSignedIn } = useAuth();
  const location = useLocation();

  // The client is rebuilt on every auth/org change, but the queue must outlive
  // those. A ref hands the latest client to a send closure that never changes.
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;

  const queueRef = useRef<AnalyticsQueue | null>(null);
  if (queueRef.current === null) {
    queueRef.current = new AnalyticsQueue({
      send: async (events) => {
        await apiClientRef.current.post(INGEST_ENDPOINT, { events });
      },
    });
  }
  const queue = queueRef.current;

  const sessionIdRef = useRef<string | null>(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = getSessionId(
      typeof window === 'undefined' ? undefined : window.sessionStorage,
    );
  }

  // Signed-out renders happen while Clerk resolves; the ingest endpoint would
  // reject those, so capture is gated rather than fired and discarded.
  const isSignedInRef = useRef(isSignedIn);
  isSignedInRef.current = isSignedIn;

  const record = useRef(
    (eventName: AnalyticsEventName, properties?: AnalyticsProperties): void => {
      if (isSignedInRef.current !== true) return;

      const event: AnalyticsEvent = {
        source: ANALYTICS_SOURCE_PORTAL,
        eventName,
        path: redactPath(window.location.pathname),
        sessionId: sessionIdRef.current ?? '',
        occurredAt: new Date().toISOString(),
        ...(properties !== undefined ? { properties } : {}),
      };

      queue.enqueue(event);
    },
  ).current;

  // Route changes, including the first render. `pathname` rather than the
  // whole location: a query-string change is a filter being typed, not a
  // navigation, and recording each keystroke as a pageview would drown the
  // signal this exists to find.
  useEffect(() => {
    record(ANALYTICS_EVENT_NAME.pageView);
  }, [location.pathname, record]);

  // Clicks, in the capture phase so a handler calling stopPropagation (menus
  // and dialogs do) cannot make the interaction invisible.
  useEffect(() => {
    const onClick = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickable = findClickable(target);
      if (clickable === null) return;

      record(ANALYTICS_EVENT_NAME.uiClick, describeElement(clickable).properties);
    };

    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, [record]);

  // Periodic flush, plus one when the tab is hidden — the last chance to ship
  // before a close. Events queued after that are lost by design; a keepalive
  // request would need its own auth plumbing to save a few final clicks.
  useEffect(() => {
    const interval = window.setInterval(() => {
      void queue.flush();
    }, FLUSH_INTERVAL_MS);

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') void queue.flush();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void queue.flush();
    };
  }, [queue]);

  return <>{children}</>;
}
